import { getProfile } from '~/services/profile'
import { getCreatorItems, getOwnerItems, getSellerListings } from '~/services/algolia'
import { isAddress, isSameAddress } from '~/utils/validation'
import { getSellsByMaker } from '~/services/rarible'

export const state = () => ({
  profile: null,
  collection: null,
  collectionNextPage: null,
  creations: null,
  creationsNextPage: null,
  listings: null,
  listingsNextPage: null,
  sellOrders: null,
  walletAddress: null,
  searchTerm: '',
})

export const getters = {
  profile: (state) => state.profile,
  collection: (state) => state.collection,
  moreCollection: (state) => !!state.collectionNextPage,
  creations: (state) => state.creations,
  moreCreations: (state) => !!state.creationsNextPage,
  listings: (state) => state.listings,
  moreListings: (state) => !!state.listingsNextPage,
  listingsCount: (state) =>
    state.sellOrders?.reduce((acc, listing) => acc + parseInt(listing.makeStock), 0) || 0,
  searchTerm: (state) => state.searchTerm,
}

export const mutations = {
  setProfile(state, profile) {
    state.profile = profile
  },
  setCollection(state, collection) {
    state.collection = collection
  },
  setCollectionNextPage(state, nextPage) {
    state.collectionNextPage = nextPage
  },
  setCreations(state, creations) {
    state.creations = creations
  },
  setCreationsNextPage(state, nextPage) {
    state.creationsNextPage = nextPage
  },
  setListings(state, listings) {
    state.listings = listings
  },
  setListingsNextPage(state, nextPage) {
    state.listingsNextPage = nextPage
  },
  setSellOrders(state, sellOrders) {
    state.sellOrders = sellOrders
  },
  setWalletAddress(state, address) {
    state.walletAddress = address
  },
  setSearchTerm(state, searchTerm) {
    state.searchTerm = searchTerm
  },
}

export const actions = {
  async getProfile({ commit, dispatch, state }, walletAddress) {
    if (!isSameAddress(state.walletAddress, walletAddress)) {
      commit('setWalletAddress', walletAddress)
      commit('setProfile', null)
      commit('setCollection', null)
      commit('setCollectionNextPage', null)
      commit('setCreations', null)
      commit('setCreationsNextPage', null)
      commit('setListings', null)
      commit('setListingsNextPage', null)
      commit('setSearchTerm', '')
    }

    if (isAddress(walletAddress)) {
      const profile = await getProfile(walletAddress)
      commit('setProfile', profile)

      await Promise.all([
        dispatch('loadCollection'),
        dispatch('loadCreations'),
        dispatch('loadListings'),
        dispatch('loadSellOrders'),
      ])
    }
  },
  async search({ dispatch }) {
    await Promise.all([
      dispatch('loadCollection'),
      dispatch('loadCreations'),
      dispatch('loadListings'),
    ])
  },
  async loadCollection({ commit, state }) {
    const { items, nextPage } = await getOwnerItems(
      state.walletAddress,
      0,
      state.searchTerm
    )
    commit('setCollection', items)
    commit('setCollectionNextPage', nextPage)
  },
  async continueCollection({ commit, state }) {
    const { items, nextPage } = await getOwnerItems(
      state.walletAddress,
      state.collectionNextPage,
      state.searchTerm
    )
    commit('setCollection', (state.collection || []).concat(items))
    commit('setCollectionNextPage', nextPage)
  },
  async loadCreations({ commit, state }) {
    const { items, nextPage } = await getCreatorItems(
      state.walletAddress,
      0,
      state.searchTerm
    )
    commit('setCreations', items)
    commit('setCreationsNextPage', nextPage)
  },
  async continueCreations({ commit, state }) {
    const { items, nextPage } = await getCreatorItems(
      state.walletAddress,
      state.creationsNextPage,
      state.searchTerm
    )
    commit('setCreations', (state.creations || []).concat(items))
    commit('setCreationsNextPage', nextPage)
  },
  async loadListings({ commit, state }) {
    const { items, nextPage } = await getSellerListings(
      state.walletAddress,
      0,
      state.searchTerm
    )
    commit('setListings', items || [])
    commit('setListingsNextPage', nextPage)
  },
  async loadSellOrders({ commit, state }) {
    const orders = await getSellsByMaker(state.walletAddress)
    commit('setSellOrders', orders)
  },
}
